import { useEffect, useRef, useState } from "react";

import {
  approveEnrollment,
  EnrollmentApprovalError,
  fetchNodeDetail,
  fetchNodePage,
  fetchNodes,
  fetchOperations,
  lookupEnrollment,
  type NodeDetail,
  type PendingEnrollment,
  saveNodeTags,
  verifySecondFactor,
} from "./api";
import { brand } from "./brand";
import {
  classifyNode,
  type FleetColumn,
  type FleetNode,
  type MaintenanceWindow,
  summarizeFleet,
} from "./domain";
import { decodeFleetQuery } from "./fleet-view-url";
import { Projects } from "./Projects";
import { Operations } from "./OperationsPage";
import { RevokeIdentity } from "./RevokeIdentity";
import {
  HealthPie,
  HealthStack,
  HistoryLine,
  NodeSummaryCards,
  ObservationHistory,
  ResourceBars,
  ServiceTopology,
} from "./Visualizations";
import "./styles.css";

type ScreenState =
  | { status: "loading" }
  | { status: "mfa"; message?: string }
  | { status: "verifying" }
  | { status: "ready"; nodes: readonly FleetNode[]; csrf: string }
  | { status: "error"; message: string };

type DetailState =
  | { status: "idle" }
  | { status: "loading"; node: FleetNode }
  | { status: "ready"; node: FleetNode; history: NodeDetail }
  | { status: "error"; node: FleetNode; message: string };

type ApprovalState =
  | { status: "idle" }
  | { status: "working" }
  | { status: "failed"; message: string };

// Approval is only offered for the exact code whose pending details the operator has seen.
type EnrollmentReview =
  | { status: "idle" }
  | { status: "loading"; code: string }
  | { status: "ready"; code: string; enrollment: PendingEnrollment }
  | { status: "failed"; code: string; message: string };

const ENROLLMENT_CODE = /^[A-Z2-9]{4}-[A-Z2-9]{4}$/u;

function enrollmentFailureMessage(error: unknown): string {
  return error instanceof EnrollmentApprovalError
    ? {
      unavailable: "That code is invalid, expired, or already approved. Check the node's enrollment status, then generate a new code if needed.",
      parent: "The selected physical host is no longer available. Select an active host and try again.",
      session: "Approval was denied. Your administrator session may have expired; refresh and sign in again. If it persists, check Cloudflare Security Events.",
      "rate-limited": "Too many approval attempts. Wait before trying again.",
      service: "Approval could not be completed. Check the service and try again; do not create another code yet.",
    }[error.reason]
    : "Approval could not be completed. Check the service and try again; do not create another code yet.";
}

function enrollmentAge(seconds: number): string {
  if (seconds < 60) return "less than a minute ago";
  const minutes = Math.floor(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
}

// Groups of four hex digits are easier to compare aloud or against `sha256sum` output.
function groupedFingerprint(fingerprint: string): string {
  return fingerprint.match(/.{1,4}/gu)?.join(" ") ?? fingerprint;
}

type FleetPage = "dashboard" | "nodes" | "projects" | "operations";

function boundedContactAge(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 525_600
    ? parsed
    : null;
}

function csvValues(value: string): string[] {
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

/** Render the personal, read-only fleet management surface. */
export function App() {
  const [screen, setScreen] = useState<ScreenState>({ status: "loading" });
  const [code, setCode] = useState("");

  const load = async (): Promise<void> => {
    try {
      const state = await fetchNodes();
      setScreen({ status: "ready", nodes: state.nodes, csrf: state.csrf });
    } catch (error: unknown) {
      setScreen(
        error instanceof Error && error.message === "second-factor-required"
          ? { status: "mfa" }
          : {
            status: "error",
            message: "The fleet inventory could not be loaded.",
          },
      );
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const submitMfa = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (screen.status === "verifying") return;
    setScreen({ status: "verifying" });
    try {
      await verifySecondFactor(code);
      await load();
    } catch {
      setScreen({
        status: "mfa",
        message:
          "That code was not accepted. Check the current code and try again.",
      });
    }
  };

  if (screen.status === "loading") {
    return (
      <main className="center">
        <img className="loading-lockup" src={brand.lockup} alt={brand.owner} />
        <p role="status">Reading node state…</p>
      </main>
    );
  }
  if (screen.status === "mfa" || screen.status === "verifying") {
    return (
      <main className="center auth-scene">
        <form className="mfa" onSubmit={(event) => void submitMfa(event)}>
          <img
            className="brand-lockup brand-lockup--auth"
            src={brand.lockup}
            alt={brand.owner}
          />
          <p className="eyebrow">Restricted system</p>
          <h1>Confirm it’s you</h1>
          <p>
            Cloudflare verified your identity. Enter the current authenticator
            code to open network operations.
          </p>
          <label htmlFor="totp">Authenticator code</label>
          <input
            id="totp"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            disabled={screen.status === "verifying"}
            value={code}
            onChange={(event) =>
              setCode(event.target.value.replace(/\D/gu, ""))}
          />
          {screen.status === "mfa" && screen.message
            ? <p role="alert" className="error">{screen.message}</p>
            : null}
          <button
            className="auth-submit"
            type="submit"
            disabled={screen.status === "verifying"}
          >
            {screen.status === "verifying" ? "Verifying…" : "Open dashboard"}
          </button>
        </form>
      </main>
    );
  }
  if (screen.status === "error") {
    return (
      <main className="center">
        <section>
          <h1>Node state unavailable</h1>
          <p role="alert">{screen.message}</p>
          <button type="button" onClick={() => void load()}>Try again</button>
        </section>
      </main>
    );
  }
  return <Fleet nodes={screen.nodes} csrf={screen.csrf} reload={load} />;
}

/** Render summary, node inventory, and operational readiness signals. */
function Fleet(
  { nodes, csrf, reload }: {
    readonly nodes: readonly FleetNode[];
    readonly csrf: string;
    readonly reload: () => Promise<void>;
  },
) {
  const [now, setNow] = useState(() => new Date());
  const summary = summarizeFleet(nodes, now);
  const [enrollmentCode, setEnrollmentCode] = useState("");
  const [enrollmentParent, setEnrollmentParent] = useState("");
  const [approvalState, setApprovalState] = useState<ApprovalState>({ status: "idle" });
  const [enrollmentReview, setEnrollmentReview] = useState<EnrollmentReview>({ status: "idle" });
  useEffect(() => {
    if (!ENROLLMENT_CODE.test(enrollmentCode)) {
      setEnrollmentReview({ status: "idle" });
      return;
    }
    let current = true;
    setEnrollmentReview({ status: "loading", code: enrollmentCode });
    lookupEnrollment(enrollmentCode).then(
      (enrollment) => {
        if (current) setEnrollmentReview({ status: "ready", code: enrollmentCode, enrollment });
      },
      (error: unknown) => {
        if (current) {
          setEnrollmentReview({
            status: "failed",
            code: enrollmentCode,
            message: enrollmentFailureMessage(error),
          });
        }
      },
    );
    return () => {
      current = false;
    };
  }, [enrollmentCode]);
  const [detail, setDetail] = useState<DetailState>({ status: "idle" });
  const [tagSaveState, setTagSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const routePage = (): FleetPage =>
    window.location.pathname === "/projects"
      ? "projects"
      : window.location.pathname === "/operations"
      ? "operations"
      : window.location.pathname === "/nodes"
      ? "nodes"
      : "dashboard";
  const [page, setPage] = useState<FleetPage>(routePage);
  const initialFilters = decodeFleetQuery(window.location.search);
  const [query, setQuery] = useState(initialFilters.query);
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [statusFilter, setStatusFilter] = useState(
    initialFilters.statuses.join(","),
  );
  const [tagFilter, setTagFilter] = useState(initialFilters.tags.join(","));
  const [sort, setSort] = useState<"name" | "status" | "last-seen">(
    initialFilters.sort,
  );
  const [projectFilter, setProjectFilter] = useState(
    initialFilters.projectIds.join(","),
  );
  const [locationFilter, setLocationFilter] = useState(
    initialFilters.locations.join(","),
  );
  const [releaseFilter, setReleaseFilter] = useState(
    initialFilters.releases.join(","),
  );
  const [capabilityFilter, setCapabilityFilter] = useState(
    initialFilters.capabilities.join(","),
  );
  const [maxAgeFilter, setMaxAgeFilter] = useState(initialFilters.maxAge);
  const defaultColumns = [
    "status",
    "name",
    "project",
    "location",
    "release",
    "disk",
    "memory",
    "backup",
    "update",
    "last-seen",
  ] as const satisfies readonly FleetColumn[];
  const requestedColumns = initialFilters.columns;
  const [columns, setColumns] = useState<readonly FleetColumn[]>(
    requestedColumns.length ? requestedColumns : defaultColumns,
  );
  const [groupBy, setGroupBy] = useState<
    "none" | "project" | "status" | "location"
  >(initialFilters.groupBy);
  const [maintenance, setMaintenance] = useState<readonly MaintenanceWindow[]>(
    [],
  );
  const [inventoryPage, setInventoryPage] = useState(1);
  const [inventoryReload, setInventoryReload] = useState(0);
  const [inventory, setInventory] = useState<
    {
      readonly status: "loading" | "error" | "ready";
      readonly nodes: readonly FleetNode[];
      readonly total: number;
      readonly pages: number;
    }
  >({ status: "loading", nodes: [], total: 0, pages: 0 });
  const defaultViewApplied = useRef(false);
  const selectedTags = tagFilter.split(",").map((tag) => tag.trim()).filter(
    Boolean,
  );
  const availableProjects = [
    ...new Map(
      nodes.flatMap((node) =>
        (node.projectIds ?? []).map((id, index) =>
          [id, node.projectNames?.[index] ?? id] as const
        )
      ),
    ).entries(),
  ];
  const availableReleases = [...new Set(nodes.map((node) => node.release))]
    .sort();
  const availableCapabilities = [
    ...new Set(nodes.flatMap((node) => node.capabilities ?? [])),
  ].sort();
  const groupLabel = (node: FleetNode): string =>
    groupBy === "project"
      ? node.projectNames?.[0] ?? "Unassigned"
      : groupBy === "status"
      ? classifyNode(node, now)
      : groupBy === "location"
      ? node.locations?.[0] ?? "Location unavailable"
      : "";
  const visibleNodes = [...inventory.nodes].sort((left, right) =>
    groupBy !== "none" && groupLabel(left) !== groupLabel(right)
      ? groupLabel(left).localeCompare(groupLabel(right))
      : sort === "last-seen"
      ? Date.parse(right.lastSeenAt) - Date.parse(left.lastSeenAt)
      : sort === "status"
      ? classifyNode(left, now).localeCompare(classifyNode(right, now))
      : left.name.localeCompare(right.name)
  );
  const detailRequest = useRef(0);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    setInventoryPage(1);
  }, [
    capabilityFilter,
    locationFilter,
    maxAgeFilter,
    projectFilter,
    releaseFilter,
    selectedTags.join(","),
    statusFilter,
    debouncedQuery,
  ]);
  useEffect(() => {
    if (page !== "nodes") return;
    let active = true;
    setInventory((current) => ({ ...current, status: "loading" }));
    void fetchNodePage({
      page: inventoryPage,
      limit: 25,
      query: debouncedQuery,
      statuses: csvValues(statusFilter),
      tags: selectedTags,
      projectIds: csvValues(projectFilter),
      locations: csvValues(locationFilter),
      releases: csvValues(releaseFilter),
      capabilities: csvValues(capabilityFilter),
      maxContactAgeMinutes: boundedContactAge(maxAgeFilter),
      sort,
    })
      .then((result) => {
        if (active) {
          setInventory({
            status: "ready",
            nodes: result.nodes,
            total: result.pagination?.total ?? result.nodes.length,
            pages: result.pagination?.pages ?? 1,
          });
        }
      })
      .catch(() => {
        if (active) {
          setInventory((current) => ({ ...current, status: "error" }));
        }
      });
    return () => {
      active = false;
    };
  }, [
    capabilityFilter,
    debouncedQuery,
    inventoryPage,
    inventoryReload,
    locationFilter,
    maxAgeFilter,
    page,
    projectFilter,
    releaseFilter,
    selectedTags.join(","),
    sort,
    statusFilter,
  ]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    const route = (): void => setPage(routePage());
    window.addEventListener("popstate", route);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("popstate", route);
    };
  }, []);
  useEffect(() => {
    if (page === "dashboard" || page === "nodes") {
      void fetchOperations().then((operations) => {
        setMaintenance(operations.maintenance);
        const view = operations.views.find((candidate) => candidate.isDefault);
        if (!defaultViewApplied.current && !window.location.search && view) {
          defaultViewApplied.current = true;
          setQuery(view.filter.query);
          setStatusFilter(view.filter.statuses.join(","));
          setTagFilter(view.filter.tags.join(","));
          setProjectFilter((view.filter.projectIds ?? []).join(","));
          setLocationFilter((view.filter.locations ?? []).join(","));
          setReleaseFilter((view.filter.releases ?? []).join(","));
          setCapabilityFilter((view.filter.capabilities ?? []).join(","));
          setMaxAgeFilter(
            view.filter.maxContactAgeMinutes === null ||
              view.filter.maxContactAgeMinutes === undefined
              ? ""
              : String(view.filter.maxContactAgeMinutes),
          );
          setSort(view.sort);
          setColumns(view.columns);
          setGroupBy(view.groupBy);
        }
      }).catch(() => setMaintenance([]));
    }
  }, [page]);
  useEffect(() => {
    if (page !== "nodes") return;
    const parameters = new URLSearchParams();
    const requestedNodeId = new URLSearchParams(window.location.search).get(
      "node",
    );
    // Preserve a report deep link while inventory filters are serialized.
    if (requestedNodeId) parameters.set("node", requestedNodeId);
    if (debouncedQuery) parameters.set("q", debouncedQuery);
    if (statusFilter) parameters.set("status", statusFilter);
    if (selectedTags.length) parameters.set("tags", selectedTags.join(","));
    if (sort !== "name") parameters.set("sort", sort);
    if (projectFilter) parameters.set("project", projectFilter);
    if (locationFilter) parameters.set("location", locationFilter);
    if (releaseFilter) parameters.set("release", releaseFilter);
    if (capabilityFilter) parameters.set("capability", capabilityFilter);
    if (maxAgeFilter) parameters.set("maxAge", maxAgeFilter);
    if (columns.join(",") !== defaultColumns.join(",")) {
      parameters.set("columns", columns.join(","));
    }
    if (groupBy !== "none") parameters.set("group", groupBy);
    window.history.replaceState(
      {},
      "",
      `/nodes${parameters.size ? `?${parameters.toString()}` : ""}`,
    );
  }, [
    capabilityFilter,
    columns.join(","),
    debouncedQuery,
    groupBy,
    locationFilter,
    maxAgeFilter,
    page,
    projectFilter,
    releaseFilter,
    selectedTags.join(","),
    sort,
    statusFilter,
  ]);
  const navigate = (
    event: React.MouseEvent<HTMLAnchorElement>,
    destination: "/" | "/nodes" | "/projects" | "/operations",
  ): void => {
    event.preventDefault();
    window.history.pushState({}, "", destination);
    setPage(
      destination === "/projects"
        ? "projects"
        : destination === "/operations"
        ? "operations"
        : destination === "/nodes"
        ? "nodes"
        : "dashboard",
    );
  };
  const navigateToNode = (
    event: React.MouseEvent<HTMLAnchorElement>,
    node: FleetNode,
  ): void => {
    if (
      event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }
    event.preventDefault();
    const destination = `/nodes?node=${encodeURIComponent(node.id)}`;
    window.history.pushState({}, "", destination);
    setDetail({ status: "idle" });
    setPage("nodes");
  };
  const approve = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (
      enrollmentReview.status !== "ready" ||
      enrollmentReview.code !== enrollmentCode
    ) {
      setApprovalState({
        status: "failed",
        message: "Check the pending node's name, architecture and key fingerprint before approving.",
      });
      return;
    }
    setApprovalState({ status: "working" });
    try {
      await approveEnrollment(enrollmentCode, csrf, enrollmentParent || null);
      setEnrollmentCode("");
      setEnrollmentParent("");
      setApprovalState({ status: "idle" });
      await reload();
      setInventoryReload((value) => value + 1);
    } catch (error: unknown) {
      setApprovalState({ status: "failed", message: enrollmentFailureMessage(error) });
    }
  };
  const openDetail = async (node: FleetNode): Promise<void> => {
    const request = ++detailRequest.current;
    setTagSaveState("idle");
    setDetail({ status: "loading", node });
    try {
      const history = await fetchNodeDetail(node.id);
      if (request === detailRequest.current) {
        setDetail({ status: "ready", node, history });
      }
    } catch {
      if (request === detailRequest.current) {
        setDetail({
          status: "error",
          node,
          message: "History could not be loaded. Try again.",
        });
      }
    }
  };
  useEffect(() => {
    const requestedNodeId = new URLSearchParams(window.location.search).get(
      "node",
    );
    const requestedNode = requestedNodeId
      ? nodes.find((node) => node.id === requestedNodeId)
      : undefined;
    if (page === "nodes" && requestedNode && detail.status === "idle") {
      void openDetail(requestedNode);
    }
  }, [detail.status, nodes, page]);
  const closeDetail = (): void => {
    detailRequest.current += 1;
    setTagSaveState("idle");
    setDetail({ status: "idle" });
    const parameters = new URLSearchParams(window.location.search);
    parameters.delete("node");
    window.history.replaceState(
      {},
      "",
      `/nodes${parameters.size ? `?${parameters.toString()}` : ""}`,
    );
  };
  const [revokedNotice, setRevokedNotice] = useState<string | null>(null);
  const identityRevoked = async (name: string): Promise<void> => {
    closeDetail();
    setRevokedNotice(`${name} was revoked. Its key can no longer upload to Fleet.`);
    await reload();
    setInventoryReload((value) => value + 1);
  };
  const saveTags = async (
    event: React.FormEvent<HTMLFormElement>,
    nodeId: string,
  ): Promise<void> => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const tags = String(values.get("tags")).split(",").map((tag) => tag.trim())
      .filter(Boolean);
    setTagSaveState("saving");
    try {
      await saveNodeTags(nodeId, tags, csrf);
      await reload();
      setInventoryReload((value) => value + 1);
      setTagSaveState("saved");
    } catch {
      setTagSaveState("error");
    }
  };
  return (
    <>
      <a className="skip-link" href="#fleet-content">Skip to node state</a>
      <main className="shell" id="fleet-content" tabIndex={-1}>
        <header>
          <img className="brand-lockup" src={brand.lockup} alt={brand.owner} />
          <p className="privacy">
            <span className="privacy-dot" />Control plane
          </p>
        </header>
        {page === "dashboard"
          ? (
            <section className="thesis" aria-labelledby="fleet-status-title">
              <div className="thesis__lead">
                <p className="eyebrow">{brand.product}</p>
                <h1 id="fleet-status-title">Fleet status at a glance</h1>
                <p className="fleet-score">
                  {summary.healthy}<span>/ {summary.total}</span>
                </p>
                <p className="status-copy">physical hosts healthy now</p>
              </div>
              <div className="fleet-pulse" aria-label="Current fleet signals">
                <p>{brand.description}</p>
                <dl>
                  <div><dt>Attention</dt><dd>{summary.warning + summary.critical}</dd></div>
                  <div><dt>Out of contact</dt><dd>{summary.stale + summary.offline}</dd></div>
                  <div><dt>Backup review</dt><dd>{nodes.filter((node) => node.backup.state !== "verified").length}</dd></div>
                  <div><dt>Updates ready</dt><dd>{nodes.filter((node) => node.update.state !== "current").length}</dd></div>
                </dl>
              </div>
            </section>
          )
          : null}
        <nav className="view-tabs" aria-label="Primary navigation">
          <a
            href="/"
            aria-current={page === "dashboard" ? "page" : undefined}
            onClick={(event) => navigate(event, "/")}
          >
            Dashboard
          </a>
          <a
            href="/nodes"
            aria-current={page === "nodes" ? "page" : undefined}
            onClick={(event) => navigate(event, "/nodes")}
          >
            Nodes &amp; reports
          </a>
          <a
            href="/projects"
            aria-current={page === "projects" ? "page" : undefined}
            onClick={(event) => navigate(event, "/projects")}
          >
            Projects
          </a>
          <a
            href="/operations"
            aria-current={page === "operations" ? "page" : undefined}
            onClick={(event) => navigate(event, "/operations")}
          >
            Operations
          </a>
        </nav>
        {page === "projects"
          ? <Projects nodes={nodes} csrf={csrf} now={now} />
          : page === "operations"
          ? <Operations nodes={nodes} csrf={csrf} now={now} />
          : (
            <>
              {page === "dashboard"
                ? (
                  <>
              {maintenance.filter((window) =>
                window.state === "scheduled" &&
                Date.parse(window.endsAt) > now.getTime()
              ).slice(0, 3).map((window) => (
                <aside className="maintenance-strip" key={window.id}>
                  <strong>
                    {Date.parse(window.startsAt) <= now.getTime()
                      ? "Maintenance active"
                      : "Upcoming maintenance"}
                  </strong>
                  <span>{window.reason}</span>
                  <time dateTime={window.endsAt}>
                    Ends {new Date(window.endsAt).toLocaleString()}
                  </time>
                </aside>
              ))}
              <section
                className="enrollment"
                aria-labelledby="enrollment-title"
              >
                <div>
                  <h2 id="enrollment-title">Enroll a node</h2>
                  <p>
                    Enter the short code shown on the newly provisioned host.
                  </p>
                </div>
                <form onSubmit={(event) => void approve(event)}>
                  <label htmlFor="enrollment-code">Approval code</label>
                  <input
                    id="enrollment-code"
                    placeholder="ABCD-2345"
                    pattern="[A-Za-z2-9]{4}-[A-Za-z2-9]{4}"
                    required
                    value={enrollmentCode}
                    onChange={(event) =>
                      setEnrollmentCode(event.target.value.toUpperCase())}
                  />
                  <label htmlFor="enrollment-parent">Physical host</label>
                  <select id="enrollment-parent" value={enrollmentParent} onChange={(event) => setEnrollmentParent(event.target.value)}>
                    <option value="">New standalone host</option>
                    {nodes.filter((node) => !node.parentNodeId).map((node) => (
                      <option key={node.id} value={node.id}>{node.name}</option>
                    ))}
                  </select>
                  <p>For VM 102, choose its Proxmox host. The VM keeps its own reporting key.</p>
                  {enrollmentReview.status === "ready" &&
                      enrollmentReview.code === enrollmentCode
                    ? (
                      <dl className="enrollment-review" aria-label="Pending node">
                        <div>
                          <dt>Name</dt>
                          <dd>{enrollmentReview.enrollment.displayName}</dd>
                        </div>
                        <div>
                          <dt>Architecture</dt>
                          <dd>{enrollmentReview.enrollment.architecture}</dd>
                        </div>
                        <div>
                          <dt>Requested</dt>
                          <dd>
                            <time dateTime={enrollmentReview.enrollment.createdAt}>
                              {enrollmentAge(enrollmentReview.enrollment.ageSeconds)}
                            </time>
                          </dd>
                        </div>
                        <div>
                          <dt>Key fingerprint (SHA-256)</dt>
                          <dd>
                            <code>
                              {groupedFingerprint(
                                enrollmentReview.enrollment.publicKeyFingerprint,
                              )}
                            </code>
                          </dd>
                        </div>
                      </dl>
                    )
                    : enrollmentReview.status === "loading"
                    ? <p role="status">Checking code…</p>
                    : enrollmentReview.status === "failed"
                    ? (
                      <p role="status" className="error">
                        {enrollmentReview.message}
                      </p>
                    )
                    : null}
                  <button type="submit" disabled={approvalState.status === "working"}>
                    {approvalState.status === "working"
                      ? "Approving…"
                      : "Approve node"}
                  </button>
                  {approvalState.status === "failed"
                    ? (
                      <p role="alert" className="error">
                        {approvalState.message}
                      </p>
                    )
                    : null}
                </form>
              </section>
              <section className="chart-grid" aria-label="Fleet visualizations">
                <HealthPie nodes={nodes} now={now} />
                <ResourceBars nodes={nodes} />
                <NodeSummaryCards
                  nodes={nodes}
                  now={now}
                  onOpen={navigateToNode}
                />
              </section>
                  </>
                )
                : null}
              {page === "nodes"
                ? (
                  <>
              <section className="nodes-page" aria-labelledby="inventory-title">
                <div className="section-heading">
                  <div>
                    <h2 id="inventory-title">Node details and reports</h2>
                    <p>
                      Current resources, readiness, backup state, and retained
                      operational history
                    </p>
                  </div>
                  <p>
                    {summary.warning + summary.critical + summary.stale +
                      summary.offline} require attention
                  </p>
                </div>
                <div className="fleet-filters">
                  <label>
                    Search nodes<input
                      type="search"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Name, project, location, version, capability, or tag"
                    />
                  </label>
                  <label>
                    Status<select
                      multiple
                      value={csvValues(statusFilter)}
                      onChange={(event) =>
                        setStatusFilter(
                          Array.from(
                            event.currentTarget.selectedOptions,
                            (option) => option.value,
                          ).join(","),
                        )}
                    >
                      <option value="healthy">Healthy</option>
                      <option value="warning">Warning</option>
                      <option value="critical">Critical</option>
                      <option value="stale">Stale</option>
                      <option value="offline">Offline</option>
                    </select>
                  </label>
                  <label>
                    Project<select
                      multiple
                      value={csvValues(projectFilter)}
                      onChange={(event) =>
                        setProjectFilter(
                          Array.from(
                            event.currentTarget.selectedOptions,
                            (option) => option.value,
                          ).join(","),
                        )}
                    >
                      {availableProjects.map(([id, name]) => (
                        <option key={id} value={id}>{name}</option>
                      ))}
                    </select>
                  </label>
                  <details className="filter-advanced">
                    <summary>Advanced filters and visible fields</summary>
                    <div className="filter-advanced__grid">
                  <label>
                    Locations contain<input
                      value={locationFilter}
                      maxLength={120}
                      placeholder="Abuja, Lagos"
                      onChange={(event) =>
                        setLocationFilter(event.target.value)}
                    />
                  </label>
                  <label>
                    Release<select
                      multiple
                      value={csvValues(releaseFilter)}
                      onChange={(event) =>
                        setReleaseFilter(
                          Array.from(
                            event.currentTarget.selectedOptions,
                            (option) => option.value,
                          ).join(","),
                        )}
                    >
                      {availableReleases.map((release) => (
                        <option key={release} value={release}>{release}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Capability<select
                      multiple
                      value={csvValues(capabilityFilter)}
                      onChange={(event) =>
                        setCapabilityFilter(
                          Array.from(
                            event.currentTarget.selectedOptions,
                            (option) => option.value,
                          ).join(","),
                        )}
                    >
                      {availableCapabilities.map((capability) => (
                        <option key={capability} value={capability}>
                          {capability}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Contact within minutes<input
                      type="number"
                      min={0}
                      max={525600}
                      value={maxAgeFilter}
                      onChange={(event) => setMaxAgeFilter(event.target.value)}
                    />
                  </label>
                  <label>
                    Tags<input
                      value={tagFilter}
                      onChange={(event) => setTagFilter(event.target.value)}
                      placeholder="camera, customer-a"
                    />
                  </label>
                  <label>
                    Sort<select
                      value={sort}
                      onChange={(event) =>
                        setSort(
                          (["name", "status", "last-seen"] as const).includes(
                              event.target.value as
                                | "name"
                                | "status"
                                | "last-seen",
                            )
                            ? event.target.value as
                              | "name"
                              | "status"
                              | "last-seen"
                            : "name",
                        )}
                    >
                      <option value="name">Name</option>
                      <option value="status">Status</option>
                      <option value="last-seen">Last seen</option>
                    </select>
                  </label>
                  <label>
                    Group nodes by<select
                      value={groupBy}
                      onChange={(event) =>
                        setGroupBy(
                          event.target.value === "project" ||
                            event.target.value === "status" ||
                            event.target.value === "location"
                            ? event.target.value
                            : "none",
                        )}
                    >
                      <option value="none">No grouping</option>
                      <option value="project">Project</option>
                      <option value="status">Status</option>
                      <option value="location">Location</option>
                    </select>
                  </label>
                  <fieldset>
                    <legend>Visible node fields</legend>
                    {defaultColumns.filter((column) =>
                      !["status", "name"].includes(column)
                    ).map((column) => (
                      <label key={column}>
                        <input
                          type="checkbox"
                          checked={columns.includes(column)}
                          onChange={(event) =>
                            setColumns(
                              event.target.checked
                                ? [...columns, column]
                                : columns.filter((candidate) =>
                                  candidate !== column
                                ),
                            )}
                        />
                        {column}
                      </label>
                    ))}
                  </fieldset>
                    </div>
                  </details>
                  {query || statusFilter || tagFilter ||
                      projectFilter || locationFilter || releaseFilter ||
                      capabilityFilter || maxAgeFilter || sort !== "name"
                    ? (
                      <button
                        type="button"
                        onClick={() => {
                          setQuery("");
                          setStatusFilter("");
                          setTagFilter("");
                          setProjectFilter("");
                          setLocationFilter("");
                          setReleaseFilter("");
                          setCapabilityFilter("");
                          setMaxAgeFilter("");
                          setSort("name");
                        }}
                      >
                        Reset filters
                      </button>
                    )
                    : null}
                </div>
                {revokedNotice
                  ? <p role="status" className="notice">{revokedNotice}</p>
                  : null}
                {inventory.status === "loading" && inventory.nodes.length > 0
                  ? (
                    <p className="sr-only" role="status">
                      Loading another inventory page…
                    </p>
                  )
                  : null}
                <div
                  className="node-list"
                  aria-busy={inventory.status === "loading"}
                >
                  {inventory.status === "loading" &&
                      inventory.nodes.length === 0
                    ? <p role="status">Loading this inventory page…</p>
                    : inventory.status === "error"
                    ? (
                      <div className="empty">
                        <h3>Inventory page unavailable</h3>
                        <p role="alert">
                          This inventory page could not be loaded. The fleet
                          summary remains unchanged.
                        </p>
                        <button
                          type="button"
                          aria-label="Retry inventory page"
                          onClick={() =>
                            setInventoryReload((value) => value + 1)}
                        >
                          Try again
                        </button>
                      </div>
                    )
                    : nodes.length === 0
                    ? (
                      <div className="empty">
                        <h3>No nodes enrolled</h3>
                        <p>
                          Run{" "}
                          <code>ha-fleet-agent enroll --name "Node name"</code>
                          {" "}
                          on a provisioned host, then approve its short code
                          above.
                        </p>
                      </div>
                    )
                    : visibleNodes.length === 0
                    ? (
                      <div className="empty">
                        <h3>No nodes match this view</h3>
                        <button
                          type="button"
                          onClick={() => {
                            setQuery("");
                            setStatusFilter("");
                            setTagFilter("");
                            setProjectFilter("");
                            setLocationFilter("");
                            setReleaseFilter("");
                            setCapabilityFilter("");
                            setMaxAgeFilter("");
                            setSort("name");
                          }}
                        >
                          Reset filters
                        </button>
                      </div>
                    )
                    : visibleNodes.map((node, index) => {
                      const status = classifyNode(node, now);
                      const heading = groupBy !== "none" &&
                          (index === 0 ||
                            groupLabel(visibleNodes[index - 1]!) !==
                              groupLabel(node))
                        ? (
                          <h3
                            className="node-group-title"
                            key={`group-${groupLabel(node)}`}
                          >
                            {groupLabel(node)}
                          </h3>
                        )
                        : null;
                      const card = (
                        <article className="node" key={node.id}>
                          <div>
                            {columns.includes("status")
                              ? (
                                <span
                                  className={`status status--${status}`}
                                  aria-hidden="true"
                                />
                              )
                              : null}
                            {columns.includes("status")
                              ? <span className="node-state">{status}</span>
                              : null}
                            {columns.includes("name")
                              ? <h3>{node.name}</h3>
                              : null}
                            <p>
                              {node.architecture}
                              {columns.includes("release")
                                ? ` · ${node.release}`
                                : ""} · {node.lifecycle}
                              {node.lifecycleMode === "adopt" ? " · Adopted" : ""}
                            </p>
                            {node.duplicateOf?.length
                              ? <p className="notice notice--warning">Same machine as another identity</p>
                              : null}
                            {columns.includes("project") &&
                                node.projectNames?.length
                              ? <p>{node.projectNames.join(" · ")}</p>
                              : null}
                            {columns.includes("location") &&
                                node.locations?.length
                              ? <p>{node.locations.join(" · ")}</p>
                              : null}
                            {node.tags?.length
                              ? <p>{node.tags.join(" · ")}</p>
                              : null}
                            <button
                              className="text-button"
                              type="button"
                              onClick={() => void openDetail(node)}
                            >
                              Open details and history
                            </button>
                          </div>
                          <dl>
                            {columns.includes("disk")
                              ? (
                                <div>
                                  <dt>Disk</dt>
                                  <dd>{node.diskUsedPercent}%</dd>
                                </div>
                              )
                              : null}
                            {columns.includes("memory")
                              ? (
                                <div>
                                  <dt>Memory</dt>
                                  <dd>
                                    {node.memoryUsedPercent === null
                                      ? "Unavailable"
                                      : `${node.memoryUsedPercent}%`}
                                  </dd>
                                </div>
                              )
                              : null}
                            {columns.includes("backup")
                              ? (
                                <div>
                                  <dt>Backup</dt>
                                  <dd>{node.backup.state}</dd>
                                </div>
                              )
                              : null}
                            {columns.includes("update")
                              ? (
                                <div>
                                  <dt>Update</dt>
                                  <dd>{node.update.state}</dd>
                                </div>
                              )
                              : null}
                            {columns.includes("last-seen")
                              ? (
                                <div>
                                  <dt>Last seen</dt>
                                  <dd>
                                    <time dateTime={node.lastSeenAt}>
                                      {new Date(node.lastSeenAt)
                                        .toLocaleString()}
                                    </time>
                                  </dd>
                                </div>
                              )
                              : null}
                          </dl>
                        </article>
                      );
                      return heading ? [heading, card] : card;
                    })}
                </div>
                {inventory.status === "ready" && inventory.total > 0
                  ? (
                    <nav
                      className="inventory-pagination"
                      aria-label="Node inventory pages"
                    >
                      <p aria-live="polite">
                        Showing {(inventoryPage - 1) * 25 + 1}–{Math.min(
                          inventoryPage * 25,
                          inventory.total,
                        )} of {inventory.total}{" "}
                        {inventory.total === 1 ? "node" : "nodes"}
                      </p>
                      <div>
                        <button
                          type="button"
                          aria-label="Previous inventory page"
                          disabled={inventoryPage <= 1}
                          onClick={() =>
                            setInventoryPage((value) => Math.max(1, value - 1))}
                        >
                          Previous
                        </button>
                        <span>
                          Page {inventoryPage} of {Math.max(1, inventory.pages)}
                        </span>
                        <button
                          type="button"
                          aria-label="Next inventory page"
                          disabled={inventoryPage >= inventory.pages}
                          onClick={() =>
                            setInventoryPage((value) =>
                              Math.min(inventory.pages, value + 1)
                            )}
                        >
                          Next
                        </button>
                      </div>
                    </nav>
                  )
                  : null}
              </section>
              {detail.status !== "idle"
                ? (
                  <section
                    className="detail"
                    aria-labelledby="detail-title"
                    aria-live="polite"
                  >
                    <div className="section-heading">
                      <h2 id="detail-title">
                        {detail.node.name} report history
                      </h2>
                      <button
                        className="text-button"
                        type="button"
                        onClick={closeDetail}
                      >
                        Close
                      </button>
                    </div>
                    {detail.status === "loading"
                      ? <p role="status">Loading recent history…</p>
                      : null}
                    {detail.status === "error"
                      ? (
                        <>
                          <p role="alert" className="error">{detail.message}</p>
                          <button
                            type="button"
                            onClick={() => void openDetail(detail.node)}
                          >
                            Try again
                          </button>
                        </>
                      )
                      : null}
                    {detail.status === "ready"
                      ? (
                        <>
                          <dl className="report-summary">
                            {detail.node.parentNodeId
                              ? <div><dt>Physical host</dt><dd>{nodes.find((node) => node.id === detail.node.parentNodeId)?.name ?? "Unavailable"}</dd></div>
                              : <>
                                  <div><dt>Proxmox guests</dt><dd>{detail.history.localGuests?.length ?? "Not reported"}</dd></div>
                                  <div><dt>Enrolled guest identities</dt><dd>{nodes.filter((node) => node.parentNodeId === detail.node.id).length}</dd></div>
                                </>}
                            <div><dt>Management</dt><dd>{detail.history.provenance?.lifecycleMode === "adopt" ? "Adopted (existing resources observed, not owned)" : detail.history.provenance ? "Installed by HA Bootstrap" : "Not reported"}</dd></div>
                            <div><dt>Recent observations</dt><dd>{detail.history.observations.length}</dd></div>
                            <div><dt>State changes</dt><dd>{detail.history.events.length}</dd></div>
                            <div><dt>Services</dt><dd>{detail.history.services.length}</dd></div>
                            <div><dt>Release</dt><dd>{detail.node.release}</dd></div>
                          </dl>
                          {detail.node.duplicateOf?.length
                            ? (
                              <p className="notice notice--warning" role="status">
                                This machine also reports as {detail.node.duplicateOf.map((id) => nodes.find((node) => node.id === id)?.name ?? "another identity").join(", ")}.
                                Revoke the stale identity so the host is counted once.
                              </p>
                            )
                            : null}
                          <p className="sr-only">
                            {detail.history.observations.length} recent observations · {detail.history.events.length} state changes
                          </p>
                          <form
                            onSubmit={(event) =>
                              void saveTags(event, detail.node.id)}
                          >
                            <label>
                              Operator tags<input
                                name="tags"
                                defaultValue={(detail.node.tags ?? []).join(
                                  ", ",
                                )}
                                pattern="[A-Za-z0-9._,-]+(, ?[A-Za-z0-9._-]+)*"
                              />
                            </label>
                            <button
                              type="submit"
                              disabled={tagSaveState === "saving"}
                            >
                              {tagSaveState === "saving"
                                ? "Saving…"
                                : "Save tags"}
                            </button>
                            {tagSaveState === "saved"
                              ? <p role="status">Tags saved.</p>
                              : null}
                            {tagSaveState === "error"
                              ? (
                                <p role="alert" className="error">
                                  Tags could not be saved. Try again.
                                </p>
                              )
                              : null}
                          </form>
                          {detail.node.projectIds?.length
                            ? (
                              <nav aria-label="Assigned project reports">
                                <span>Project reports:</span>
                                {detail.node.projectIds.map((
                                  projectId,
                                  index,
                                ) => (
                                  <a
                                    key={projectId}
                                    href={`/projects#project-${
                                      encodeURIComponent(projectId)
                                    }`}
                                  >
                                    {detail.node.projectNames?.[index] ??
                                      projectId}
                                  </a>
                                ))}
                              </nav>
                            )
                            : null}
                          <div className="chart-grid">
                            <HistoryLine detail={detail.history} />
                            <HealthStack detail={detail.history} />
                          </div>
                          {!detail.node.parentNodeId
                            ? (
                              <section className="local-guests" aria-labelledby="local-guests-title">
                                <h3 id="local-guests-title">Proxmox guests on this host</h3>
                                {detail.history.localGuests?.length
                                  ? (
                                    <ul>
                                      {detail.history.localGuests.map((guest) => (
                                        <li key={guest.id}>
                                          <strong>{guest.role === "haos" ? "Home Assistant OS" : "Docker appliance"}</strong>
                                          <span>{guest.kind === "vm" ? "VM" : "LXC"} {guest.id}</span>
                                          <span>{guest.status}</span>
                                          {guest.origin === "operator-created" ? <span className="tag">Adopted · observed only</span> : null}
                                        </li>
                                      ))}
                                    </ul>
                                  )
                                  : <p>{detail.history.localGuests === null ? "No Proxmox guest inventory has been reported yet. Refresh the host agent and collect a new snapshot." : "No Proxmox guests were selected for this host."}</p>}
                                <p>These guests share the physical host’s Fleet identity. Separately enrolled media VMs appear as guest identities.</p>
                              </section>
                            )
                            : null}
                          <ObservationHistory detail={detail.history} />
                          <h3>Service inventory and dependencies</h3>
                          {detail.history.services.length === 0
                            ? (
                              <p>
                                No service inventory reported by this agent.
                              </p>
                            )
                            : (
                              <div className="table-scroll">
                                <table>
                                  <thead>
                                    <tr>
                                      <th>Service</th>
                                      <th>Image</th>
                                      <th>Immutable digest</th>
                                      <th>State</th>
                                      <th>Health</th>
                                      <th>Restarts</th>
                                      <th>Dependencies</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detail.history.services.map((service) => (
                                      <tr key={service.catalog_id}>
                                        <td>{service.display_name}</td>
                                        <td>{service.image}</td>
                                        <td>
                                          {service.image_digest ??
                                            "Unavailable"}
                                        </td>
                                        <td>{service.state}</td>
                                        <td>{service.health}</td>
                                        <td>{service.restart_count}</td>
                                        <td>
                                          {service.dependencies.join(", ") ||
                                            "None"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          <ServiceTopology detail={detail.history} />
                          <ol>
                            {detail.history.events.length === 0
                              ? (
                                <li>
                                  No health transitions recorded in the
                                  retention window.
                                </li>
                              )
                              : detail.history.events.map((event) => (
                                <li
                                  key={`${event.occurred_at}-${event.message}`}
                                >
                                  <time dateTime={event.occurred_at}>
                                    {new Date(event.occurred_at)
                                      .toLocaleString()}
                                  </time>{" "}
                                  — {event.message}
                                </li>
                              ))}
                          </ol>
                          <RevokeIdentity
                            key={detail.node.id}
                            nodeId={detail.node.id}
                            nodeName={detail.node.name}
                            csrf={csrf}
                            onRevoked={identityRevoked}
                          />
                        </>
                      )
                      : null}
                  </section>
                )
                : null}
                  </>
                )
                : null}
            </>
          )}
      </main>
    </>
  );
}
